import { useMemo, useState } from 'react';
import type { FindingDto, FindingSeverity } from '@wave/shared';
import { hasRight, useAuthStore } from '../../auth/store';
import { useArchiveCoverage, useArchiveFindings } from '../../api/hooks';
import { useResolveFinding } from '../../api/mutations';
import { AskAgentButton } from '../AskAgentButton';
import { KpiCard } from '../../pages/AuditPage';
import { COVERAGE_WINDOW_DAYS, coveragePercent, coverageWindow, findingRowLabel } from './findingText';
import {
  btnGhostSm,
  btnPrimarySm,
  cardL2,
  filterActive,
  filterIdle,
  pageSubtitle,
  subSectionTitle,
  tableCell,
  tableCls,
  tableHead,
  tableHeadCell,
  tableRow,
  tableWrapper,
  textareaCls,
} from '../../lib/styles';

/** Раздел таблицы: «Расхождения» (всё, кроме unlinked-op) или «Без привязки» (спека §6). */
type Section = 'mismatch' | 'unlinked';
type SeverityFilter = 'all' | FindingSeverity;

const SEVERITY_META: Record<FindingSeverity, { label: string; cls: string }> = {
  high: { label: 'Критично', cls: 'text-danger' },
  medium: { label: 'Важно', cls: 'text-warning' },
  low: { label: 'Незначительно', cls: 'text-text2' },
};

/** «сегодня 14:32» / «12 авг 14:32» — момент находки в локали оператора. */
function fmtMoment(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === today.toDateString()) return `сегодня ${time}`;
  return `${d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })} ${time}`;
}

/**
 * Вкладка «Целостность» на `/audit` — находки проверок целостности архива
 * сообщений бота (C1..C5) плюс отдельно операции без привязки к заявке/пере-
 * воду (`unlinked-op`). Источник данных независим от вкладки «Балансы»
 * (`archiveApiUrl` в конфигурации балансов) — свои состояния загрузки/
 * «не настроено» (спека §6).
 */
export function ArchiveFindingsTab() {
  const rights = useAuthStore((s) => s.rights);
  const canAck = hasRight(rights, 'audit.ack');

  // Разделы тянутся РАЗНЫМИ запросами (спека §6): «Расхождения» — всё, кроме
  // unlinked-op, «Без привязки» — только они. Одним запросом счётчики врали бы —
  // ежедневные unlinked-op вытеснили бы настоящие находки за границу страницы.
  const mismatchQ = useArchiveFindings({ status: 'open', excludeKind: 'unlinked-op' });
  const unlinkedQ = useArchiveFindings({ status: 'open', kind: 'unlinked-op' });
  // Окно — 7 суток (окно проверок §4.4): без него сервер отдаёт всю таблицу
  // покрытия за ретеншн 365 суток, а среднее за год не покажет провал сегодня.
  const win = useMemo(() => coverageWindow(new Date()), []);
  const { data: coverage } = useArchiveCoverage(win.from, win.to);
  const resolve = useResolveFinding();

  const [section, setSection] = useState<Section>('mismatch');
  const [severity, setSeverity] = useState<SeverityFilter>('all');
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  const [comment, setComment] = useState('');

  const mismatchItems = useMemo(
    () => (mismatchQ.data?.pages ?? []).flatMap((p) => p.items),
    [mismatchQ.data],
  );
  const unlinkedItems = useMemo(
    () => (unlinkedQ.data?.pages ?? []).flatMap((p) => p.items),
    [unlinkedQ.data],
  );
  const visible = useMemo(() => {
    if (section === 'unlinked') return unlinkedItems;
    return severity === 'all' ? mismatchItems : mismatchItems.filter((f) => f.severity === severity);
  }, [section, severity, mismatchItems, unlinkedItems]);

  const activeQ = section === 'unlinked' ? unlinkedQ : mismatchQ;
  const data = mismatchQ.data?.pages[0];
  const isLoading = mismatchQ.isLoading || unlinkedQ.isLoading;
  const error = mismatchQ.error ?? unlinkedQ.error;

  const coverageAgg = useMemo(() => {
    const rows = coverage?.rows ?? [];
    const journalRows = rows.reduce((s, r) => s + r.journalRows, 0);
    const matched = rows.reduce((s, r) => s + r.matched, 0);
    return { percent: coveragePercent({ journalRows, matched }), unparsedTotal: coverage?.unparsedTotal ?? 0 };
  }, [coverage]);

  if (isLoading && !data) {
    return <div className="text-text2">Загрузка…</div>;
  }
  if (data && !data.configured) {
    return (
      <p className={pageSubtitle}>
        Источник архива сообщений не настроен — задайте адрес ленты (<code>archiveApiUrl</code>) на странице
        «Балансы».
      </p>
    );
  }

  function startResolve(id: number) {
    setResolvingId(id);
    setComment('');
  }

  return (
    <>
      {error && (
        <div className="mb-4 rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-4 py-3 text-sm text-danger">
          Не удалось загрузить находки: {(error as Error).message}
        </div>
      )}

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard
          label={`Покрытие за ${COVERAGE_WINDOW_DAYS} дней`}
          value={coverageAgg.percent !== null ? `${coverageAgg.percent}%` : 'нет данных'}
          cls="text-warning"
        />
        <KpiCard label="Не распознано сообщений" value={coverageAgg.unparsedTotal} cls="text-text2" />
        <KpiCard
          label="Расхождения"
          value={countLabel(mismatchItems.length, mismatchQ.hasNextPage)}
          cls="text-danger"
        />
        <KpiCard
          label="Без привязки"
          value={countLabel(unlinkedItems.length, unlinkedQ.hasNextPage)}
          cls="text-text2"
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={section === 'mismatch' ? filterActive : filterIdle}
          onClick={() => setSection('mismatch')}
        >
          Расхождения ({countLabel(mismatchItems.length, mismatchQ.hasNextPage)})
        </button>
        <button
          type="button"
          className={section === 'unlinked' ? filterActive : filterIdle}
          onClick={() => setSection('unlinked')}
        >
          Без привязки ({countLabel(unlinkedItems.length, unlinkedQ.hasNextPage)})
        </button>
        {section === 'mismatch' && (
          <>
            <span className="mx-1 h-5 w-px bg-border" />
            {(['all', 'high', 'medium', 'low'] as const).map((s) => (
              <button
                key={s}
                type="button"
                className={severity === s ? filterActive : filterIdle}
                onClick={() => setSeverity(s)}
              >
                {s === 'all' ? 'Любая серьёзность' : SEVERITY_META[s].label}
              </button>
            ))}
          </>
        )}
      </div>

      <div className={tableWrapper}>
        <table className={tableCls}>
          <thead className={tableHead}>
            <tr>
              <th className={tableHeadCell}>Чат</th>
              <th className={tableHeadCell}>{section === 'mismatch' ? 'Заявка / валюта' : 'Валюта'}</th>
              <th className={tableHeadCell}>Момент</th>
              <th className={tableHeadCell}>Находка</th>
              {canAck && <th className={tableHeadCell}>Гашение</th>}
            </tr>
          </thead>
          <tbody>
            {visible.map((f) => (
              <FindingRows
                key={f.id}
                finding={f}
                canAck={canAck}
                isResolving={resolvingId === f.id}
                comment={comment}
                onComment={setComment}
                onStartResolve={() => startResolve(f.id)}
                onCancelResolve={() => setResolvingId(null)}
                onSubmitResolve={() =>
                  resolve.mutate(
                    { id: f.id, comment: comment.trim() },
                    { onSuccess: () => setResolvingId(null) },
                  )
                }
                pending={resolve.isPending}
                submitError={resolve.isError && resolvingId === f.id ? (resolve.error as Error).message : null}
              />
            ))}
            {visible.length === 0 && (
              <tr>
                <td className={`${tableCell} text-text2`} colSpan={canAck ? 5 : 4}>
                  {activeQ.hasNextPage
                    ? 'На загруженных страницах ничего не подошло — список неполон, дочитайте его кнопкой «Показать ещё».'
                    : section === 'mismatch'
                      ? 'Расхождений нет.'
                      : 'Операций без привязки нет.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {activeQ.hasNextPage && (
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            className={btnGhostSm}
            disabled={activeQ.isFetchingNextPage}
            onClick={() => activeQ.fetchNextPage()}
          >
            {activeQ.isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
          </button>
          <span className="text-xs text-text2">
            Список неполон: загружено {visible.length}, счётчики со знаком «+» — не итоговые.
          </span>
        </div>
      )}
    </>
  );
}

/** «12» или «12+», когда сервер отдал не все страницы: точное число неизвестно. */
function countLabel(loaded: number, hasMore: boolean): string {
  return hasMore ? `${loaded}+` : String(loaded);
}

function FindingRows({
  finding,
  canAck,
  isResolving,
  comment,
  onComment,
  onStartResolve,
  onCancelResolve,
  onSubmitResolve,
  pending,
  submitError,
}: {
  finding: FindingDto;
  canAck: boolean;
  isResolving: boolean;
  comment: string;
  onComment: (v: string) => void;
  onStartResolve: () => void;
  onCancelResolve: () => void;
  onSubmitResolve: () => void;
  pending: boolean;
  submitError: string | null;
}) {
  const sev = SEVERITY_META[finding.severity];
  const colSpan = canAck ? 5 : 4;
  return (
    <>
      <tr className={tableRow}>
        <td className={tableCell}>
          <div className="font-medium text-text">{finding.chatTitle ?? finding.chatId ?? '—'}</div>
          <div className={`text-xs ${sev.cls}`}>{sev.label}</div>
        </td>
        <td className={`${tableCell} font-mono`}>{findingRowLabel(finding)}</td>
        <td className={`${tableCell} text-xs text-text2`}>{fmtMoment(finding.occurredAt)}</td>
        <td className={tableCell}>
          {finding.details.summary}
          {!finding.resolvedAt && (
            <div className="mt-1">
              <AskAgentButton request={{ kind: 'finding-triage', findingId: finding.id }} label="🤖 Проверить агентом" />
            </div>
          )}
        </td>
        {canAck && (
          <td className={tableCell}>
            {isResolving ? (
              <span className="text-text-subtle">…</span>
            ) : (
              <button type="button" className={btnGhostSm} onClick={onStartResolve}>
                Погасить
              </button>
            )}
          </td>
        )}
      </tr>
      {isResolving && (
        <tr className={tableRow}>
          <td className={tableCell} colSpan={colSpan}>
            <div className={cardL2}>
              <div className={subSectionTitle}>Погасить находку</div>
              <textarea
                className={`${textareaCls} mt-2 w-full`}
                rows={2}
                maxLength={500}
                placeholder="Почему находка закрыта без правки (обязательно)…"
                value={comment}
                onChange={(e) => onComment(e.target.value)}
              />
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  className={btnPrimarySm}
                  disabled={!comment.trim() || pending}
                  onClick={onSubmitResolve}
                >
                  Сохранить
                </button>
                <button type="button" className={btnGhostSm} onClick={onCancelResolve}>
                  Отмена
                </button>
              </div>
              {submitError && <div className="mt-2 text-xs text-danger">{submitError}</div>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
